import { cn } from '@/lib/utils';
import {
  getAvatarColor,
  getDisplayName,
  getInitials,
  getInitialsFromParts,
} from '@/utils/avatarUtils';

type UserAvatarSize = 'sm' | 'md' | 'lg';
/** 'auto' (default) picks a deterministic solid color per user. 'brand' uses the
 *  fixed light-purple/purple-text treatment from the design system (e.g. sidebar). */
type UserAvatarVariant = 'auto' | 'brand';

type UserAvatarProps = {
  name?: string;
  firstName?: string;
  lastName?: string;
  size?: UserAvatarSize;
  className?: string;
  variant?: UserAvatarVariant;
};

const sizeClasses: Record<UserAvatarSize, string> = {
  sm: 'size-8 text-xs',
  md: 'size-[34px] text-[11px]',
  lg: 'size-10 text-sm',
};

const UserAvatar = ({
  name,
  firstName,
  lastName,
  size = 'sm',
  className,
  variant = 'auto',
}: UserAvatarProps) => {
  const displayName = name ?? getDisplayName(firstName, lastName);
  const initials = name ? getInitials(name) : getInitialsFromParts(firstName, lastName);
  const isBrand = variant === 'brand';

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-bold',
        isBrand ? 'bg-m4-purple-100 text-m4-purple-700' : 'text-white',
        sizeClasses[size],
        className
      )}
      style={isBrand ? undefined : { backgroundColor: getAvatarColor(displayName) }}
      aria-hidden={!displayName}
    >
      {initials}
    </div>
  );
};

export default UserAvatar;
